/**
 * Subcontractor trailers — units a haulier has left with us, planned alongside
 * our own fleet.
 *
 * <p>
 * Backed by the `SubcontractorTrailers` table in MyDirectory. Deliberately not
 * `Resource` rows: they belong to the subcontractor, carry no compliance state,
 * and a drop unit's lifetime is a single job. See the BE entity for why a nullable
 * owner column on `Resource` was the wrong answer.
 * </p>
 */
import type { SubcontractorTrailer, TrailerPresenceValue } from '@my/directory-api';
import type { ResourceCardVM, SubcontractorChannel } from './planResources';

/**
 * Why the unit is on our yard, which decides what happens to it after a job:
 * `Drop` is a one-shot, `Hire` returns to the pool free until the hire ends.
 *
 * <p>
 * The wire vocabulary is used directly rather than a lowercase local union — one
 * fewer adapter between the form, the card and the API.
 * </p>
 */
export type TrailerPresence = TrailerPresenceValue;

export const PRESENCE_LABEL: Record<TrailerPresence, string> = {
  Drop: 'DROP',
  Hire: 'HIRE',
};

/**
 * Badge palette for the two presences.
 *
 * <p>
 * Amber for drop — it's the one with a caveat, and amber is already the app's
 * "heads up" colour. Blue for hire, reading as settled / ongoing. Neither is
 * otherwise in play on these cards: the channel badge isn't rendered here (the
 * corner carries presence instead) and subcontractor units have no cert chips,
 * so blue is free.
 * </p>
 */
export const PRESENCE_ACCENT: Record<TrailerPresence, string> = {
  Drop: 'bg-amber-100 text-amber-700',
  Hire: 'bg-blue-100 text-blue-700',
};

/** What the add / edit form collects. Mirrors the create request minus the org id. */
export interface SubcontractorTrailerDraft {
  unitName: string;
  presence: TrailerPresence;
  /** `yyyy-MM-dd`. Required on Hire, null on Drop. */
  onHireFrom: string | null;
  /** `yyyy-MM-dd`. Null = open-ended hire. */
  onHireTo: string | null;
  subcontractorPartyId: string;
  organisationEquipmentTypeId: string | null;
}

/**
 * Field-level problems with a draft, keyed by field name. Empty object = valid.
 * Returned rather than thrown so the form can mark every bad field at once.
 */
export type DraftErrors = Partial<
  Record<'unitName' | 'subcontractorPartyId' | 'onHireFrom' | 'onHireTo', string>
>;

export function validateDraft(draft: SubcontractorTrailerDraft): DraftErrors {
  const errors: DraftErrors = {};
  if (!draft.unitName.trim()) errors.unitName = 'Give the unit a name or registration.';
  if (!draft.subcontractorPartyId) {
    errors.subcontractorPartyId = 'Select the subcontractor supplying this unit.';
  }
  // Only hire units have a period, so the date is required there and meaningless
  // on a drop. Mirrors the BE validator so the form never posts a body the
  // server will refuse.
  if (draft.presence === 'Hire' && !draft.onHireFrom) {
    errors.onHireFrom = 'Set the date the hire starts.';
  }
  if (draft.onHireFrom && draft.onHireTo && draft.onHireTo < draft.onHireFrom) {
    errors.onHireTo = "The hire can't end before it starts.";
  }
  return errors;
}

export const isValidDraft = (draft: SubcontractorTrailerDraft) =>
  Object.keys(validateDraft(draft)).length === 0;

/**
 * Normalise a draft for the wire: trim the name, and clear both hire dates on a
 * drop so a value left over from toggling presence can't be posted — the BE
 * rejects a drop that carries a hire period rather than ignoring it.
 */
export function normaliseDraft(draft: SubcontractorTrailerDraft): SubcontractorTrailerDraft {
  const isHire = draft.presence === 'Hire';
  return {
    ...draft,
    unitName: draft.unitName.trim(),
    onHireFrom: isHire ? draft.onHireFrom : null,
    onHireTo: isHire ? draft.onHireTo : null,
  };
}

/**
 * Build the strip/Gantt card for a subcontractor trailer.
 *
 * <p>
 * Deliberately not routed through `resourceCard` — that takes a `Resource`, and
 * these aren't one. The overlap is only in the view model.
 * </p>
 */
export function subcontractorTrailerCard({
  trailer,
  equipmentName,
  channel,
}: {
  trailer: SubcontractorTrailer;
  /** Equipment-type label resolved from the catalogue key, or null when unspecified. */
  equipmentName: string | null;
  /** The supplying haulier's channel, so the card can carry it like theirs. */
  channel: SubcontractorChannel | null;
}): ResourceCardVM {
  // The badge already names the presence, so the note carries the detail that
  // goes with it rather than repeating the word.
  const presenceNote =
    trailer.presence === 'Drop'
      ? 'Single job only'
      : trailer.onHireFrom
        ? trailer.onHireTo
          ? `${trailer.onHireFrom} → ${trailer.onHireTo}`
          : `From ${trailer.onHireFrom}`
        : null;

  return {
    id: trailer.id,
    title: trailer.unitName,
    // The supplying haulier is the second line — knowing whose trailer it is
    // matters more than what kind it is.
    subtitle: trailer.subcontractorName ?? 'Unknown subcontractor',
    meta: equipmentName,
    // Not one of ours, so no `Resource.type` and no equipment type chip — the
    // type is on the meta line instead.
    resourceType: null,
    typeChip: null,
    caps: [],
    // A subcontractor's unit is available to plan the moment it's registered;
    // there's no status to be inactive.
    available: true,
    showTacho: false,
    linkedVehicle: null,
    note: presenceNote,
    // Belongs to the haulier's yard, not ours, so the depot filter must never
    // hide it (same treatment as the subcontractors pool).
    homeSiteId: null,
    depotLabel: null,
    currentLocationLabel: null,
    odometerKm: null,
    alerts: [],
    linkedDriverNames: [],
    channel,
    cityLabel: null,
    addressLabel: null,
    countryCode: null,
    // Capacity is a property of the haulier, not of one of their trailers.
    assignedCount: null,
    acceptedCount: null,
    assignmentCap: null,
    // Presence, not "SUB": every card in this pool is a subcontractor's unit
    // (the tab says so, and the amber haulier line reinforces it), so spending
    // the most prominent slot on that would say nothing. Drop-vs-hire is what
    // changes the planner's decision.
    topBadge: {
      label: PRESENCE_LABEL[trailer.presence],
      accent: PRESENCE_ACCENT[trailer.presence],
    },
    // Needed when assigning: a subcontracted slot must name who supplies it.
    subcontractorPartyId: trailer.subcontractorPartyId,
    isSubcontractorUnit: true,
  };
}
